import AutoResizableTextarea from "./AutoResizableTextarea";
import { DataState } from "../context/DataProvider";
import React, { useEffect, useState, useMemo, useCallback, useRef } from "react";
import MessageImages from "./chat/MessageImages";
import ToolApprovalUI from "./chat/ToolApprovalUI";
import ToolResponseApprovalUI from "./chat/ToolResponseApprovalUI";
import ReasoningDropdown from "./chat/ReasoningDropdown";
import ToolCallsUI from "./chat/ToolCallsUI";
import TextReasoningDropdown from "./chat/TextReasoningDropdown";
import ActionTrackerEmbedded from "./chat/ActionTrackerEmbedded";
import { ArrowDown } from 'lucide-react';
import MessageAttachments from "./chat/MessageAttachments";
import ToolsResponseUI from "./chat/ToolsResponseUI";

import MarkdownRenderer from "./MarkdownRenderer";
import { useVirtualizer } from "@tanstack/react-virtual";

const MemoMarkdown = React.memo(MarkdownRenderer);

// Stable key helper
// Prefers a message-level ID so React never re-mounts stable rows.
// Falls back to role+index only for synthetic rows (reason, tool_calls_ui, streamingResponse).
const getChatKey = (chat, index) => {
  if (chat?._id) return chat._id;
  if (chat?.tool_call_id) return `tool-${chat.tool_call_id}`;
  if (chat?.role === "reason") return `reason-${index}`;
  if (chat?.role === "tool_calls_ui") return `tcu-${index}`;
  // streaming placeholder — always last, always the same key
  if (chat?.role === "assistant" && !chat?._id) return "streaming-response";
  return `chat-${index}`;
};

// Individual chat row — memoised so the virtualizer
// only re-renders rows whose props actually changed.
const ChatRow = React.memo(function ChatRow({
  chat,
  userInfo,
  userGoogleId,
  toolCallApprovalList,
  currentToolResponseForReference,
}) {
  const hasContent =
    ((chat.type && chat.role === "bot") ||
      (chat.content && typeof chat.content === "string") ||
      (chat.reasoning && chat.role === "assistant") ||
      (chat.content &&
        Array.isArray(chat.content) &&
        chat.content.length !== 0 &&
        chat.content[0].text)) &&
    chat.role !== "tool";

  return (
    <>
      {hasContent && (
        <div className={`flex gap-1 mb-3 flex-col ${chat?.isSubtask ? "" : ""}`}>
          <div className={`flex w-full flex-col gap-2 ${chat.role === 'user' ? 'items-end' : 'items-start'}`}>
            {chat.role === "user" && Array.isArray(chat.content) && (
              <div className="w-full">
                <MessageImages content={chat.content} />
              </div>
            )}
            {chat.attachments && chat.attachments.length > 0 && (
              <div className="w-full">
                <MessageAttachments attachments={chat.attachments} />
              </div>
            )}
          </div>
          <div className={`flex gap-1 ${chat?.isSubtask ? "justify-end w-[90%]" : "w-full"}`}>
            <div className={`flex gap-1 ${userInfo && chat.role === "user" ? "flex-row-reverse ml-auto " : "flex-row mr-auto w-full"} items-start`}>
              <div className="flex-1 min-w-0">
                <div className={`rounded-xl ${chat.role !== "user" ? "bg-[#0E0E0E] bg-opacity-0 px-1 py-1.5" : "bg-[#2F2F2F] px-2.5 py-1.5 ml-auto"}`}>
                  <div className="break-words">
                    {(chat.reasoning && chat.role === "assistant") ? (
                      <TextReasoningDropdown reasoningText={chat.reasoning} isReasoning={chat.is_reasoning} />
                    ) : null}
                    {chat.content && typeof chat.content === "string" && (
                      <MemoMarkdown
                        isAssistant={chat.role !== 'user'}
                        attachments={chat.attachments}
                        userGoogleId={userGoogleId}
                        markdown={!chat.content.includes("TASK STATUS:") ? chat.content : chat.content.split("TASK STATUS:")[0].trim()}
                      />
                    )}
                    {chat.content && Array.isArray(chat.content) && chat.content.length > 0 && chat.content[0].text && (
                      <MemoMarkdown
                        isAssistant={chat.role !== 'user'}
                        attachments={chat.attachments}
                        userGoogleId={userGoogleId}
                        markdown={!chat.content[0].text.includes("TASK STATUS:") ? chat.content[0].text : chat.content[0].text.split("TASK STATUS:")[0].trim()}
                      />
                    )}
                    {chat.role === "bot" && chat.type === "session" && (
                      <div className="flex items-center gap-2">
                        <div className="flex flex-shrink-0 rounded items-center justify-center w-5 h-5 p-0.5 bg-white">
                          <img src={"https://oqlous.inqbator.in/assets/oqlous.svg"} alt="Trigger icon" />
                        </div>
                        <MemoMarkdown markdown={`${chat.trigger_name}`} userGoogleId={userGoogleId} />
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
      {chat.role === "tool" && <ToolsResponseUI tool={chat} />}
      {chat.role === "assistant" && Array.isArray(chat.tool_calls) && (
        <ActionTrackerEmbedded currentToolsStatus={chat.tool_calls} toolCallApprovalList={toolCallApprovalList} />
      )}
      {chat.role === "assistant" && Array.isArray(chat.tool_calls) && (
        <ToolCallsUI currentToolResponseForReference={currentToolResponseForReference} tool_calls={chat.tool_calls} />
      )}
      {chat.role === "reason" && <ReasoningDropdown reasonCalls={chat.tool_calls} />}
    </>
  );
});

const ChatList = ({
  isDraggingOver = false,
  tempImages = [],
  setTempImages = () => { },
  justUploadedImages = false,
  setJustUploadedImages = () => { },
  processAndUploadFiles = () => { }
}) => {

  //context
  const {
    conversations,
    setConversations,
    input,
    setInput,
    messageContainerRef,
    streamingResponse,
    handleStreamSend,
    userInfo,
    userGoogleId,
    setToolCallApprovalList,
    toolCallApprovalList,
    currentToolResults,
    setCurrentToolResults,
    currentToolCallStatus,
    currentReasoningCalls,
    currentReasoningToolStreamingData,
    currentToolResponseForReference,
    currentToolCallStatusMap,
    setScrollToToolId, scrollToToolId,
    statusMessage,
  } = DataState();

  //states
  const [disapprovalReasons, setDisapprovalReasons] = useState({});
  const [showScrollButton, setShowScrollButton] = useState(false);

  // The scroll container — wired to both the virtualizer and messageContainerRef
  const scrollContainerRef = useRef(null);

  // Memoised callbacks

  const scrollToBottom = useCallback(() => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollTo({
        top: scrollContainerRef.current.scrollHeight,
        behavior: 'smooth',
      });
    }
  }, []);

  const handleUpdate = useCallback((toolId, updatedToolCall) => {
    setToolCallApprovalList(prevList =>
      prevList.map(toolCall =>
        toolCall.id === toolId ? updatedToolCall : toolCall
      )
    );
  }, [setToolCallApprovalList]);

  const updateChatListWithApprovals = useCallback((updatedApprovalList) => {
    if (!conversations.length) return;
    const lastChat = conversations[conversations.length - 1];
    if (lastChat && lastChat.tool_calls && lastChat.tool_calls.length > 0) {
      const conversations_temp = conversations;
      conversations_temp[conversations.length - 1] = {
        ...lastChat,
        tool_calls: updatedApprovalList,
      };
      setConversations([...conversations_temp]);
    }
  }, [conversations, setConversations]);

  const handleDisapprove = useCallback((toolCallId) => {
    const reason = disapprovalReasons[toolCallId]?.trim();
    if (!reason) {
      alert("Please provide a reason for disapproval.");
      return;
    }
    setToolCallApprovalList((prevList) => {
      const updatedList = prevList.map((toolCall) =>
        toolCall.id === toolCallId
          ? { ...toolCall, approved: true, disapproved: true, disapprovalReason: reason }
          : toolCall
      );
      const allHandled = updatedList.every((toolCall) => !toolCall.approvalNeeded || toolCall.approved);
      if (allHandled) {
        updateChatListWithApprovals(updatedList);
        return [];
      }
      return updatedList;
    });
    setDisapprovalReasons((prev) => ({ ...prev, [toolCallId]: "" }));
  }, [disapprovalReasons, setToolCallApprovalList, updateChatListWithApprovals]);

  const handleReasonChange = useCallback((toolCallId, value) => {
    setDisapprovalReasons((prev) => ({ ...prev, [toolCallId]: value }));
  }, []);

  const handleApprove = useCallback((toolCallId) => {
    setToolCallApprovalList((prevList) => {
      const updatedList = prevList.map((toolCall) =>
        toolCall.id === toolCallId ? { ...toolCall, approved: true } : toolCall
      );
      const allApproved = updatedList.every((toolCall) => toolCall.approvalNeeded === false || toolCall.approved === true);
      if (allApproved) {
        updateChatListWithApprovals(updatedList);
        return [];
      }
      return updatedList;
    });
  }, [updateChatListWithApprovals, setToolCallApprovalList]);

  const handleSend = useCallback((message) => {
    if (message) {
      setInput(message);
      // React state updates are async, so handleStreamSend might need to read message directly
      // but handleStreamSend inside DataProvider uses `input` state. 
      // To fix the immediate state use, we'll configure handleStreamSend to optionally accept the input string.
    }
    handleStreamSend(null, message);
    setTimeout(() => {
      scrollToBottom();
    }, 500);
  }, [setInput, scrollToBottom, handleStreamSend]);

  // allConversations — base structure that changes infrequently
  // Note: currentReasoningToolStreamingData is RAF-throttled in DataProvider
  // so this memo recalculates at most ~60fps, not per-chunk.

  const baseConversations = useMemo(() => {
    const result = [];
    let lastUserChatIndex = -1;
    let isLastChatToolCalls = false;
    conversations.forEach((conversation, i) => {
      if (conversation?.role === "user") {
        lastUserChatIndex = i;
      } else if (conversation?.role === "assistant") {
        isLastChatToolCalls = conversation?.tool_calls && Array.isArray(conversation?.tool_calls) && conversation?.tool_calls.length > 0;
        if (conversation?.tool_calls && Array.isArray(conversation?.tool_calls) && conversation?.tool_calls.length > 0) {
          conversation.tool_calls = (conversation.tool_calls || []).map((toolCall) => (currentToolCallStatusMap.get(toolCall.id) ? { ...toolCall, status: currentToolCallStatusMap.get(toolCall.id).status, running_phase: currentToolCallStatusMap.get(toolCall.id).running_phase } : toolCall));
        }
      }
      result.push(conversation);
    });
    if (currentReasoningCalls?.length > 0 && lastUserChatIndex !== -1) {
      result.splice(lastUserChatIndex + 1, 0, {
        role: "reason",
        tool_calls: currentReasoningCalls,
        _hasReasoningSlot: true,
      });
      lastUserChatIndex = lastUserChatIndex + 1;
    }
    if (currentToolCallStatus?.length > 0 && lastUserChatIndex !== -1) {
      result.splice(lastUserChatIndex + 1, 0, {
        role: "tool_calls_ui",
        tool_calls: currentToolCallStatus,
        isCurrent: isLastChatToolCalls,
      });
    }
    return { result, lastUserChatIndex };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [conversations, currentReasoningCalls, currentToolCallStatusMap]);

  // Merge streaming reasoning data into base conversations only when it changes.
  // This is a lightweight pass — only touches the reason row.
  const allConversations = useMemo(() => {
    const { result, lastUserChatIndex } = baseConversations;
    if (!currentReasoningToolStreamingData) return result;

    // If a reason row already exists, append streaming data to it
    const hasReasonRow = result.some((conv) => conv._hasReasoningSlot);
    if (hasReasonRow) {
      return result.map((conv) => {
        if (conv._hasReasoningSlot) {
          return {
            ...conv,
            tool_calls: [...conv.tool_calls, currentReasoningToolStreamingData],
          };
        }
        return conv;
      });
    }

    // No reason row yet but streaming data exists — inject one
    if (lastUserChatIndex !== -1) {
      const merged = [...result];
      merged.splice(lastUserChatIndex + 1, 0, {
        role: "reason",
        tool_calls: [currentReasoningToolStreamingData],
      });
      return merged;
    }

    return result;
  }, [baseConversations, currentReasoningToolStreamingData]);

  // Combine allConversations + streamingResponse into one flat array for the virtualizer
  const virtualItems = useMemo(() => {
    return [...allConversations, streamingResponse];
  }, [allConversations, streamingResponse]);

  // Virtualizer
  const virtualizer = useVirtualizer({
    count: virtualItems.length,
    getScrollElement: () => scrollContainerRef.current,
    // Initial estimate; the virtualizer will measure and correct after render
    estimateSize: () => 120,
    // Enable dynamic height measurement
    measureElement:
      typeof window !== 'undefined' && navigator.userAgent.indexOf('Firefox') === -1
        ? (element) => element?.getBoundingClientRect().height
        : undefined,
    overscan: 5, // Render 5 extra items above/below the viewport for smooth scroll
    getItemKey: (index) => getChatKey(virtualItems[index], index),
  });

  // Scroll-to-tool helper

  useEffect(() => {
    if (!scrollToToolId) return;
    const element = document.querySelector(`[data-tool-id='${scrollToToolId}']`);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'center' });
      element.classList.add('highlight-scroll');
      setTimeout(() => {
        element.classList.remove('highlight-scroll');
        setScrollToToolId(null);
      }, 1500);
    } else {
      setScrollToToolId(null);
    }
  }, [scrollToToolId, setScrollToToolId]);

  // Show scroll-to-bottom button

  useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container) return;

    const handleScroll = () => {
      const { scrollTop, scrollHeight, clientHeight } = container;
      const distanceFromBottom = scrollHeight - scrollTop - clientHeight;
      setShowScrollButton(distanceFromBottom > 150);
    };

    container.addEventListener('scroll', handleScroll);
    handleScroll();
    return () => container.removeEventListener('scroll', handleScroll);
  }, []);

  // Auto-scroll to bottom when new messages arrive 
  useEffect(() => {
    if (virtualItems.length > 0) {
      // Only auto-scroll if user is already near the bottom
      const container = scrollContainerRef.current;
      if (!container) return;
      const { scrollTop, scrollHeight, clientHeight } = container;
      const distanceFromBottom = scrollHeight - scrollTop - clientHeight;
      if (distanceFromBottom < 300) {
        virtualizer.scrollToIndex(virtualItems.length - 1, { align: 'end', behavior: 'smooth' });
      }
    }
  }, [virtualItems.length]);

  return (
    <>
      <div className="h-full w-full relative grid grid-rows-[1fr_auto]">
        <div
          ref={(el) => {
            scrollContainerRef.current = el;
            // Also honour the external messageContainerRef from context
            if (messageContainerRef) {
              if (typeof messageContainerRef === 'function') {
                messageContainerRef(el);
              } else {
                messageContainerRef.current = el;
              }
            }
          }}
          className="h-full w-full overflow-y-auto transparent-scrollbar"
        >
          {/* Virtual scroll container */}
          <div
            style={{
              height: `${virtualizer.getTotalSize()}px`,
              width: '100%',
              position: 'relative',
            }}
          >
            <div
              className="max-w-6xl w-full mx-auto px-4"
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                transform: `translateY(${virtualizer.getVirtualItems()[0]?.start ?? 0}px)`,
              }}
            >
              {virtualizer.getVirtualItems().map((virtualRow) => {
                const chat = virtualItems[virtualRow.index];
                return (
                  <div
                    key={virtualRow.key}
                    data-index={virtualRow.index}
                    ref={virtualizer.measureElement}
                    className="pb-4 first:pt-6"
                  >
                    <ChatRow
                      chat={chat}
                      userInfo={userInfo}
                      userGoogleId={userGoogleId}
                      toolCallApprovalList={toolCallApprovalList}
                      currentToolResponseForReference={currentToolResponseForReference}
                    />
                  </div>
                );
              })}

              {/* These are always rendered after the virtual list — no change in behaviour */}
              {statusMessage && (
                <div className="flex flex-row items-start w-full gap-1 mb-3 mr-auto h-72">
                  <div className="flex-1 min-w-0">
                    <div className="flex flex-col gap-1 rounded-xl bg-transparent px-1 py-1.5">
                      <div className="flex items-center gap-2 font-bold text-zinc-200 text-sm">
                        <div className="w-6"></div>
                        <span className="font-bold">Oqulus AI</span>
                      </div>
                      <div className="flex items-center gap-2 font-bold text-zinc-200">
                        <img
                          src="../assets/logo.png"
                          className="w-6 animate-rotatePause"
                          alt="logo"
                        />
                        <span>{statusMessage}</span>
                      </div>
                    </div>
                  </div>
                </div>
              )}
              <ToolApprovalUI
                handleUpdate={handleUpdate}
                toolCallApprovalList={toolCallApprovalList}
                handleApprove={handleApprove}
                disapprovalReasons={disapprovalReasons}
                handleReasonChange={handleReasonChange}
                handleDisapprove={handleDisapprove}
              />
              <ToolResponseApprovalUI
                currentToolResults={currentToolResults}
                setCurrentToolResults={setCurrentToolResults}
              />
            </div>
          </div>
        </div>

        <div className="flex-shrink-0 min-w-0 relative">
          {showScrollButton && (
            <button
              onClick={scrollToBottom}
              className="absolute left-1/2 -translate-x-1/2 -top-14 z-50 bg-zinc-800 hover:bg-zinc-700 text-white rounded-full p-2.5 shadow-lg transition-all duration-200 border border-zinc-600 animate-fade-in-fast"
              aria-label="Scroll to bottom"
            >
              <ArrowDown className="w-4 h-4" />
            </button>
          )}
          <AutoResizableTextarea
            setInput={setInput}
            input={input}
            handleSend={handleSend}
            isDraggingOver={isDraggingOver}
            tempImages={tempImages}
            justUploadedImages={justUploadedImages}
            setJustUploadedImages={setJustUploadedImages}
            processAndUploadFiles={processAndUploadFiles}
            setTempImages={setTempImages}
          />
        </div>
      </div>
    </>
  );
};

export default React.memo(ChatList);